import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ChangePasswordStep2FormValues, changePasswordStep2Schema } from "../../schema/auth/ChangePasswordStep2Schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, TextField, CircularProgress } from "@mui/material"
import { AuthBox, HorizontalLayout, MainContainer, VerticalLayout } from "../../common/common-layout"
import { useForm } from "react-hook-form"
import BTBox from "../../common/components/BTBox"
import theme from '../../common/theme';
import loginCi from "../../assets/icons/login_ci.svg"
import { TitleNormal } from "../../common/common-text"
import { authStore } from "../../store/AuthStore"
import { requestResetPassword } from "../../service/AuthService"
import DialogIconInfo from '../../assets/icons/dialog_icon_info.svg'
import { dialogStore } from "../../store/DialogStore"
import BaseLayout from "../BaseLayout"

const ChangePasswordStep2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm<ChangePasswordStep2FormValues>({
        resolver: zodResolver(changePasswordStep2Schema),
    });

    // 회원가입 처리 함수
    const handleChangePassword = async (data: ChangePasswordStep2FormValues) => {
        if (isSubmitting) return;

        /// 모든 입력값이 유효할 떄만 진행
        if (Object.keys(errors).length > 0) {
            setError("입력Form 에러");
            return;
        }

        const password = watch('password');
        const confirmPassword = watch('confirmPassword');
        const verificationId = authStore.getChangePasswordVerificationId();

        if (password && password !== "" && password === confirmPassword && verificationId) {
            try {
                setIsSubmitting(true);              // ✅ 시작
                await requestResetPassword(email, password, verificationId);
                dialogStore.showDialog({
                    title: '알림',
                    message: '비밀번호 변경이 완료되었습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        console.log('확인');
                        navigate('/login');
                    },
                    onClose: () => {
                        console.log('닫기');
                        navigate('/login');
                    },
                });

                return;
            } catch (error) {
                dialogStore.showDialog({
                    title: '알림',
                    message: `비밀번호 변경이 실패하였습니다 - 사유: ${error}`,
                    onConfirm: () => {
                        console.log('확인');
                        navigate('/login');
                    },
                    onClose: () => {
                        console.log('닫기');
                        navigate('/login');
                    },
                });
                return;
            } finally {
                setIsSubmitting(false);             // ✅ 종료
            }
        } else if (!password || password === "") {
            dialogStore.showDialog({
                title: '알림',
                message: '신규 비밀번호를 입력해주세요.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (password !== confirmPassword) {
            dialogStore.showDialog({
                title: '알림',
                message: '신규 비밀번호가 일치하지 않습니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        }

        dialogStore.showDialog({
            title: '알림',
            message: '이메일 인증 만료로 인하여 비밀번호 변경에 실패하였습니다.\n다시 진행해 주세요.',
            icon: <img src={DialogIconInfo} />,
            onConfirm: () => {
                console.log('확인');
                navigate('/change-password/step1');
            },
            onClose: () => {
                console.log('닫기');
                navigate('/change-password/step1');
            },
        });
    };

    const handleMoveLogin = () => {
        navigate("/login");
    };

    return (
        <BaseLayout>
            <MainContainer backgroundColor='#14B446'>
                <AuthBox style={{ width: '516px' }}>
                    <VerticalLayout>
                        <form onSubmit={handleSubmit(handleChangePassword)} style={{ width: 516 }}>
                            <BTBox height="20px" />
                            <HorizontalLayout style={{ justifyContent: 'center', width: '100%' }}>
                                <img src={loginCi} />
                            </HorizontalLayout>
                            <BTBox height='50px' />
                            <TitleNormal color='#8a92a6'>신규 비밀번호</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    type="password"
                                    {...register("password")}
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: errors.password ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                        },
                                    }}
                                />
                            </HorizontalLayout>
                            <BTBox height='20px' />
                            <TitleNormal color='#8a92a6'>신규 비밀번호 확인</TitleNormal>
                            <BTBox height='8px' />
                            <HorizontalLayout>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    type="password"
                                    {...register("confirmPassword")}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword?.message}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: errors.confirmPassword ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.bbTech,
                                            },
                                        },
                                    }}
                                />
                            </HorizontalLayout>

                            {error && <p style={{ color: "red" }}>{error}</p>}

                            <BTBox height='30px' />
                            <Button
                                type="submit"
                                disabled={isSubmitting}                     // ✅ 비활성화
                                style={{
                                    width: '100%',
                                    height: '59px',
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    backgroundColor: theme.colors.bbTech, // 원하는 색상
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer', // ✅ 커서
                                    outline: 'none',
                                    position: 'relative',                             // ✅ 스피너 오버레이
                                }}
                            >
                                <span style={{ visibility: isSubmitting ? 'hidden' : 'visible' }}>
                                    비밀번호 변경
                                </span>
                                {isSubmitting && (
                                    <CircularProgress
                                    size={24}
                                    sx={{ color: 'white', position: 'absolute', inset: 0, margin: 'auto' }}
                                    />
                                )}
                            </Button>
                            <BTBox height='30px' />
                        </form>
                        <HorizontalLayout style={{ justifyContent: 'center', width: '100%' }}>
                            <Button
                                type="button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    margin: 0,
                                    fontSize: '16px',
                                    color: '#3A57E8',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    outline: 'none',
                                    textTransform: 'none',
                                    lineHeight: '1',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 'auto',
                                }}
                                onClick={handleMoveLogin}
                            >
                                로그인으로 돌아가기
                            </Button>
                        </HorizontalLayout>
                        <BTBox height='20px' />
                    </VerticalLayout>
                </AuthBox>

            </MainContainer>
        </BaseLayout>
    );
};

export default ChangePasswordStep2;